/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useEffect, useState } from 'react';

/**
 * Shared mobile-breakpoint hook for the navigation shell (W2 / #683).
 *
 * Below this width the top workflow-group bar is too cramped for 8 tabs, so the
 * shell collapses to a hamburger + slide-in drawer and hides the contextual
 * sidebar. The value is **provisional** — #684 (responsive breakpoints policy)
 * owns the final number; keep it here as the single source of truth so the whole
 * shell reacts to one constant.
 */
export const NAV_MOBILE_BREAKPOINT = 860;

/**
 * Returns true when the viewport is narrower than `breakpoint` (default
 * {@link NAV_MOBILE_BREAKPOINT}). SSR-safe: assumes desktop until mounted.
 */
export function useIsMobile(breakpoint: number = NAV_MOBILE_BREAKPOINT): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);

  return isMobile;
}
